import styled from '@emotion/styled';
import { Field, Link, TextInput } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

export interface EmbedParentSelectorProps {
  parentElementSelector: string | null | undefined;
  autoValidate: boolean;
  onChange: (k: string) => void;
}

const StyledField = styled(Field)`
  width: 100%;
`;

const StyledLink = styled(Link)`
  svg {
    width: 10px;
    height: 10px;
    min-width: 10px;
    min-height: 10px;
  }
`;

export const EmbedParentSelector = ({
  parentElementSelector,
  autoValidate,
  onChange,
}: EmbedParentSelectorProps): JSX.Element => {
  const [isPristine, setPristine] = useState(true);
  const { t } = useTranslation('feedback');

  const invalidText = useMemo(() => {
    if (isPristine && !autoValidate) {
      return;
    }

    const isSelectorEmpty =
      parentElementSelector === undefined ||
      parentElementSelector === null ||
      parentElementSelector.length === 0;

    if (isSelectorEmpty) {
      return t('wizard.section.appearance.parent-element-selector.empty-selector-error');
    }

    if (parentElementSelector.length > 400) {
      return t('wizard.section.appearance.parent-element-selector.selector-too-long-error');
    }

    return undefined;
  }, [autoValidate, parentElementSelector, t, isPristine]);

  return (
    <StyledField
      inputId="parent-element-selector"
      labelText={t('wizard.section.appearance.parent-element-selector.label')}
      helperText={
        <>
          {t('wizard.section.appearance.parent-element-selector.helper-text')}{' '}
          <StyledLink
            kind="inline"
            as="a"
            isExternal
            href="https://help.hotjar.com/hc/en-us/articles/10405253676823"
          >
            {t('wizard.section.appearance.parent-element-selector.helper-link')}
          </StyledLink>
        </>
      }
      isInvalid={invalidText !== undefined}
      invalidText={invalidText}
      size="l"
    >
      <TextInput
        id="parent-element-selector"
        placeholder={t('wizard.section.appearance.parent-element-selector.placeholder')}
        value={parentElementSelector ?? ''}
        onChange={(evt) => {
          onChange(evt.target.value);
          setPristine(false);
        }}
        onBlur={
          parentElementSelector !== undefined && parentElementSelector !== null
            ? () => onChange(parentElementSelector)
            : undefined
        }
        isInvalid={invalidText !== undefined}
      />
    </StyledField>
  );
};
